import { SpeakableSpeechMark } from '../tts.constants';

type ProcessedSpeechMark = SpeakableSpeechMark & {
  processed?: boolean;
};

function findSpeechMark({
  speechMarks,
  wordIndex,
  combinedText,
  lastTime,
  hasPlayedSpeechMarks,
}: {
  speechMarks: ProcessedSpeechMark[];
  wordIndex: number;
  combinedText: string;
  lastTime: number;
  hasPlayedSpeechMarks: boolean;
}) {
  const speechMarkIndex = hasPlayedSpeechMarks
    ? speechMarks.findIndex(
        mark =>
          mark.value === combinedText &&
          !mark.processed &&
          mark.indexInHtmlBody === wordIndex
      )
    : speechMarks.findIndex(
        mark =>
          mark.value === combinedText && !mark.processed && mark.time > lastTime
      );

  const previousSpeechMark = speechMarks[speechMarkIndex - 1];

  return speechMarkIndex > 0 && !previousSpeechMark?.processed
    ? null
    : speechMarks[speechMarkIndex];
}

function injectListNumbers(htmlString: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  function processList(
    ol: HTMLOListElement,
    level: number,
    counters: number[]
  ) {
    let counter = 1;

    ol.querySelectorAll(':scope > li').forEach(li => {
      const number = counter;

      li.classList.add('tts-list-item');

      // Find all direct child <p> elements and inject the same number
      // since the speechmarks from poly take this approach
      li.querySelectorAll(':scope > p').forEach(p => {
        if (p.firstChild && p.firstChild.nodeType === Node.TEXT_NODE) {
          p.firstChild.nodeValue = number + ' ' + p.firstChild.nodeValue;
        } else {
          const textNode = document.createTextNode(number + ' ');
          p.insertBefore(textNode, p.firstChild);
        }
      });

      // if the li contains a nested ol, process it recursively
      const nestedOl = li.querySelector('ol');
      if (nestedOl) {
        processList(nestedOl, level + 1, [...counters, counter]);
      }

      counter++;
    });
  }

  doc.querySelectorAll('body > ol').forEach(ol => {
    processList(ol as HTMLOListElement, 1, []);
  });

  const wrapperDiv = doc.createElement('div');
  wrapperDiv.className = 'tts-text-parsed';
  while (doc.body.firstChild) {
    wrapperDiv.appendChild(doc.body.firstChild);
  }
  doc.body.appendChild(wrapperDiv);

  return doc.body.innerHTML;
}

export function ttsCreateSpeakableContent(
  html: string,
  speechMarks: ProcessedSpeechMark[]
) {
  const hasPlayedSpeechMarks = speechMarks.some(s => s.played);
  const speechMarksCopy = speechMarks.map(s => ({ ...s }));

  const htmlWithListNumbers = injectListNumbers(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlWithListNumbers, 'text/html');

  let lastTime = 0;
  let wordIndex = 0;

  function processNode(node: ChildNode) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const fragment = document.createDocumentFragment();
      let i = 0;

      while (i < text.length) {
        let combinedText = text[i];
        let matchIndex = i;

        // matching progressively longer character sequences
        while (matchIndex < text.length) {
          const speechMark = findSpeechMark({
            speechMarks: speechMarksCopy,
            wordIndex,
            combinedText,
            lastTime,
            hasPlayedSpeechMarks,
          });

          if (speechMark) {
            const span = document.createElement('span');
            span.id = `tts-${speechMark.time}`;
            span.className = 'tts-word';
            span.textContent = combinedText;
            fragment.appendChild(span);

            speechMark.processed = true;
            speechMark.indexInHtmlBody = wordIndex;
            lastTime = speechMark.time;

            i = matchIndex + 1; // Move past the matched sequence
            wordIndex++;
            break;
          }

          matchIndex++;
          if (matchIndex < text.length) {
            combinedText += text[matchIndex];
          }
        }

        // no match was found, add the character as normal text
        if (matchIndex === text.length) {
          fragment.appendChild(document.createTextNode(text[i]));
          i++;
        }
      }

      node.replaceWith(fragment);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(child => processNode(child));
    }
  }

  processNode(doc.body);

  return {
    processedHtml: doc.body.innerHTML,
    speechMarks: speechMarksCopy,
  };
}
